// ─── SectionCustomers ───────────────────────────────────────────────────────
// Customer stories on the homepage. The full pool lives at /customers; this
// section shows the strongest 6 + a wide "read them all" CTA strip so the
// homepage can prove social proof without becoming its own /customers page.
//
// Layout: a 2x2 hero tile (Vinny) on the left, five supporting tiles filling
// the right 3-col x 2-row block, and a CTA tile bottom-right. Each tile carries
// its own blue-to-orange gradient (see CustomerBentoTile), so the colour stays
// inside the boxes and never bleeds into the grid gaps.
// ────────────────────────────────────────────────────────────────────────────
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { PhotoTile, StoryModal, type Tile } from '../CustomerBentoTile'

// Six biggest names on the homepage — Vinny hero + Freeday, Altura, PagBank,
// Backbase, Deloitte. All have real wordmarks (no serif-text fallbacks). The
// other six stories live on /customers; the CTA tile pulls visitors there.
const tiles: Tile[] = [
  {
    key: 'vinny',
    company: 'Vinny',
    industry: 'Conversational support · UK',
    logo: '/logos/vinny.png',
    logoH: 26,
    headshot: '/customers/cstories3.webp',
    quote:
      'When a customer has an issue we spin up a simulation and give them really concrete evidence it has been fixed.',
    story:
      'Vinny ships a customer-support agent for high-volume retail. Before LangWatch, regression checks were ad-hoc and slow. Now simulations replay the exact failure case against every new build, so a customer who reported a bug on Monday gets a verifiable fix by Tuesday.',
    name: 'Rory Buchanan',
    role: 'Founder & CTO',
  },
  {
    key: 'freeday',
    company: 'Freeday',
    industry: 'AI labour platform · Netherlands',
    logo: '/logos/freeday.webp',
    logoH: 30,
    headshot: '/customers/cstories5.webp',
    quote:
      'Testing went from afterthought to starting point. What took half a day takes ten minutes.',
    story:
      'Freeday builds digital workers for back-office teams. Their engineers used to ship and pray; now every prompt change triggers a full scenario sweep before merge. The faster loop means more agents shipped per week, not fewer bugs caught later.',
    name: 'Gijs Gubbels',
    role: 'AI Engineer',
  },
  {
    key: 'altura',
    company: 'Altura',
    industry: 'AI for procurement · Netherlands',
    logo: '/logos/altura.svg',
    logoH: 22,
    headshot: '/customers/cstories6.webp',
    headshotObjectPosition: 'center 15%',
    quote:
      'We did a full deep dive of every solution. Moved from Langfuse to LangWatch and love the collaboration features.',
    tileQuote: 'Moved from Langfuse to LangWatch and love the collaboration features.',
    story:
      'Altura evaluated every observability and eval tool on the market before picking LangWatch. The decision was the collaboration surface: domain experts can annotate traces and define evals in the same place engineers debug them.',
    name: 'Martijn Schouten',
    role: 'AI Engineer',
  },
  {
    key: 'pagbank',
    company: 'PagBank',
    industry: 'Digital banking · Brazil',
    logo: '/logos/pagbank.webp',
    logoH: 30,
    headshot: '/customers/cstories4.webp',
    headshotShift: '14px',
    locked: true,
    anon: true,
    quote:
      'LangWatch gives us confidence in every AI-driven interaction. Critical when you handle payments at scale.',
    story:
      'PagBank uses LangWatch to monitor and evaluate every AI interaction across millions of customers. Want to know how it works in production at that scale? Book a call with our team and we will walk you through the deployment patterns.',
  },
  {
    key: 'hyperfox',
    company: 'Hyperfox',
    industry: 'Operational AI · Belgium',
    logo: '/logos/hyperfox.svg',
    logoH: 26,
    headshot: '/customers/cstories1.webp',
    headshotShift: '14px',
    quote:
      'Every increment we ship now, whether a feature or a bug fix, we have much more confidence.',
    story:
      'Hyperfox automates order processing for B2B distributors. Their team uses LangWatch to lock in agent behaviour before each release; every increment ships against the same battery of real-world scenarios, so regressions are caught at PR time.',
    name: 'Clara de Smet',
    role: 'Lead AI',
  },
  {
    key: 'backbase',
    company: 'Backbase',
    industry: 'Engagement banking · Netherlands',
    logo: '/logos/backbase.webp',
    logoH: 20,
    locked: true,
    quote:
      'LangWatch helps us ship AI features with confidence, faster, safer, no surprises in production.',
    story:
      'Backbase rolls AI assistants into the banking experiences used by the world\'s largest financial institutions. Curious how the platform plugs into a regulated banking stack? Book a call with our team and we will walk you through it.',
    name: 'Deepak Pandey',
    role: 'VP Technology',
  },
]

function CtaTile({ className = '' }: { className?: string }) {
  return (
    <a
      href="/customers"
      className={`group relative flex h-full min-h-[260px] sm:min-h-[220px] md:min-h-[180px] flex-col justify-end overflow-hidden rounded-2xl p-4 ring-1 ring-inset ring-white/25 backdrop-blur-xl backdrop-saturate-150 transition-all duration-500 hover:rounded-[2px] hover:ring-white/40 md:p-5 ${className}`}
      style={{
        // Same dark mesh palette as the customer tiles (WarpAccent 'dark').
        backgroundImage:
          'radial-gradient(130% 130% at 85% 15%, rgba(91,141,239,0.85) 0%, rgba(30,58,138,0.55) 40%, transparent 72%), radial-gradient(115% 115% at 5% 95%, rgba(201,123,58,0.5) 0%, transparent 52%), linear-gradient(150deg, #0c0f1c 0%, #0a0a0c 100%)',
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.22),transparent_55%)]" />

      <div className="relative">
        <div className="font-serif text-[19px] leading-[1.1] font-medium text-paper md:text-[21px]">
          All customer stories
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-paper/90 transition group-hover:text-paper">
          Read them
          <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
        </div>
      </div>
    </a>
  )
}

export function SectionCustomers() {
  const [open, setOpen] = useState<Tile | null>(null)

  return (
    <section id="customers" className="relative scroll-mt-0 border-t border-ink-100 bg-paper text-ink-900">
      <div className="container-rail relative z-10 py-24 md:py-32">
        <div>
          <h2 className="display text-[30px] leading-[1.06] text-ink-950 md:text-[44px]">
            Trusted by teams shipping{' '}
            <span className="italic text-rainbow-shift">mission-critical AI.</span>
          </h2>
          <p className="mt-7 text-[17px] leading-[1.7] text-ink-600 md:text-[18.5px]">
            CTOs, engineers, AI architects and product leaders shipping AI
            they can trust in production.
          </p>
        </div>

        {/* Magazine bento: Vinny is the 2×2 hero feature on the left, five
            supporting customers fill the right 3-col × 2-row block, a slim
            CTA tile closes the bottom-right. The moving mesh sits ONLY
            behind this block — the rest of the section is clean paper, so
            the animation reads as "the cards are alive", not as section
            ambience. */}
        <div className="relative mt-8 md:mt-10">
          <div className="relative grid grid-cols-1 gap-2 sm:grid-cols-2 md:auto-rows-[250px] md:grid-cols-5 md:gap-2.5">
            <PhotoTile
              t={tiles[0]}
              index={0}
              className="md:col-span-2 md:row-span-2"
              featured
              onOpen={() => setOpen(tiles[0])}
            />
            <PhotoTile t={tiles[1]} index={1} onOpen={() => setOpen(tiles[1])} />
            <PhotoTile t={tiles[2]} index={2} onOpen={() => setOpen(tiles[2])} />
            <PhotoTile t={tiles[3]} index={3} onOpen={() => setOpen(tiles[3])} />
            <PhotoTile t={tiles[4]} index={4} onOpen={() => setOpen(tiles[4])} />
            <PhotoTile t={tiles[5]} index={5} onOpen={() => setOpen(tiles[5])} />
            <CtaTile />
          </div>
        </div>
      </div>

      {open && <StoryModal tile={open} onClose={() => setOpen(null)} />}
    </section>
  )
}
